import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import BookButton from "@/components/BookButton";
import SocialProof from "@/components/SocialProof";
import { PROPERTY } from "@/lib/constants";
import { useSEO, breadcrumbLd } from "@/lib/seo";
import { trackEvent } from "@/lib/analytics";

export default function Contact() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", check_in: "", check_out: "", guests: 1, message: "",
  });
  const [busy, setBusy] = useState(false);

  useSEO({
    title: "Contact Us — Westbridge Inn & Suites, Carrollton MO",
    description:
      "Call our 24-hour front desk at (660) 542-2988 or send an inquiry. 1408 N US-65, Carrollton, MO 64633. Email: carrollton@westbridgehotelgroup.com",
    path: "/contact",
    jsonLd: breadcrumbLd([{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }]),
  });

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    trackEvent("form_submit", "inquiry");
    try {
      await api.post("/inquiries", { ...form, guests: parseInt(form.guests || 1, 10) });
      toast.success("Thank you — we'll be in touch within one business day.");
      setForm({ name: "", email: "", phone: "", check_in: "", check_out: "", guests: 1, message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send your inquiry. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="pt-36 pb-16 bg-canvas">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <p className="font-mono-label text-ember mb-4">Get in touch</p>
            <h1 className="font-display text-6xl md:text-7xl text-ink leading-[0.98]">
              We answer<br /><span className="italic">the phone.</span>
            </h1>
          </div>
          <div className="md:col-span-4">
            <p className="text-ink-soft leading-relaxed">
              Front desk hotline is staffed 24 hours a day, every day. For non-urgent inquiries — group
              bookings, accessibility questions, longer stays — use the form on this page.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-canvas pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12">
          {/* FORM */}
          <form onSubmit={submit} className="md:col-span-7 bg-sand p-8 md:p-12 space-y-6" data-testid="contact-form">
            <p className="font-mono-label text-ember">Send an inquiry</p>
            <div className="grid sm:grid-cols-2 gap-6">
              <Field label="Your name" required>
                <input required type="text" value={form.name} onChange={handle("name")} className="wb-input" data-testid="contact-name" />
              </Field>
              <Field label="Email address" required>
                <input required type="email" value={form.email} onChange={handle("email")} className="wb-input" data-testid="contact-email" />
              </Field>
              <Field label="Phone (optional)">
                <input type="tel" value={form.phone} onChange={handle("phone")} className="wb-input" data-testid="contact-phone" />
              </Field>
              <Field label="Number of guests">
                <input type="number" min="1" max="20" value={form.guests} onChange={handle("guests")} className="wb-input" data-testid="contact-guests" />
              </Field>
              <Field label="Check-in (optional)">
                <input type="date" value={form.check_in} onChange={handle("check_in")} className="wb-input" data-testid="contact-checkin" />
              </Field>
              <Field label="Check-out (optional)">
                <input type="date" value={form.check_out} onChange={handle("check_out")} className="wb-input" data-testid="contact-checkout" />
              </Field>
            </div>
            <Field label="How can we help?" required>
              <textarea required rows={5} value={form.message} onChange={handle("message")} className="wb-input resize-none" data-testid="contact-message" />
            </Field>
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <button type="submit" disabled={busy} className="btn-ember disabled:opacity-60" data-testid="contact-submit">
                {busy ? "Sending…" : "Send inquiry"}
              </button>
              <p className="font-mono-label text-ink-muted">…or reserve instantly →</p>
              <BookButton label="Reserve" testid="contact-form-book" />
            </div>
            <style>{`
              .wb-input {
                width: 100%;
                background: transparent;
                border: none;
                border-bottom: 1px solid hsl(var(--rule-strong));
                padding: 12px 0;
                color: hsl(var(--ink-primary));
                font-family: "Manrope", sans-serif;
                font-size: 1rem;
                transition: border-color 200ms;
                border-radius: 0;
              }
              .wb-input:focus { outline: none; border-bottom-color: hsl(var(--ember)); }
            `}</style>
          </form>

          {/* CONTACT INFO */}
          <aside className="md:col-span-5 space-y-10">
            <div>
              <p className="font-mono-label text-ember mb-3">Visit us</p>
              <address className="not-italic font-display text-3xl text-ink leading-snug">
                {PROPERTY.street}<br />{PROPERTY.cityState}
              </address>
            </div>
            <div>
              <p className="font-mono-label text-ember mb-3">Reservations · 24h</p>
              <a href={`tel:${PROPERTY.phone.replace(/[^\d+]/g, "")}`} className="font-display text-4xl text-ink hover:text-ember transition-colors" data-testid="contact-phone-link">
                {PROPERTY.phone}
              </a>
            </div>
            <div>
              <p className="font-mono-label text-ember mb-3">Office</p>
              <a href={`tel:${PROPERTY.office.replace(/[^\d+]/g, "")}`} className="font-display text-2xl text-ink-soft hover:text-ember transition-colors">
                {PROPERTY.office}
              </a>
            </div>
            <div>
              <p className="font-mono-label text-ember mb-3">Email</p>
              <a href={`mailto:${PROPERTY.email}`} className="font-display text-2xl text-ink-soft hover:text-ember transition-colors break-all" data-testid="contact-email-link">
                {PROPERTY.email}
              </a>
            </div>
            <div className="aspect-[4/3] bg-dune overflow-hidden">
              <iframe
                title="Westbridge Inn & Suites map"
                src={`https://maps.google.com/maps?q=${encodeURIComponent(PROPERTY.street + " " + PROPERTY.cityState)}&z=14&output=embed`}
                className="w-full h-full border-0"
                loading="lazy"
              />
            </div>
            <SocialProof variant="compact" />
          </aside>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="font-mono-label text-ink-muted">
        {label}{required && <span className="text-ember"> *</span>}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
